import {useMutation} from '@tanstack/react-query';
import {UseFormReturn} from 'react-hook-form';
import {BackendResponse} from '../../http/backend-response/backend-response';
import {onFormQueryError} from '../../errors/on-form-query-error';
import {useNavigate} from '../../ui/navigation/use-navigate';
import {apiClient} from '../../http/query-client';
import {useAuth} from '../use-auth';
import {useCallback} from 'react';
import {setBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import { appPushNotificationManager } from '@common/core/capacitor/app-push-notifications-manager';
import { saveToken, extractToken, getDeviceSync } from '../bearer-token';
import {Capacitor} from '@capacitor/core';
import { SavePassword } from 'capacitor-ios-autofill-save-password';

const isNative = Capacitor.isNativePlatform();
const isIos = Capacitor.getPlatform() === 'ios';

interface LoginResponse extends BackendResponse {
  bootstrapData: string;
  two_factor: false;
}
interface TwoFactorResponse {
  two_factor: true;
}

type Response = LoginResponse | TwoFactorResponse;

export interface LoginPayload {
  email: string;
  password: string;
  remember: boolean;
  token_name?: string;
}

export function useLogin(form: UseFormReturn<LoginPayload>) {
  const handleSuccess = useHandleLoginSuccess();
  return useMutation({
    mutationFn: login,
    onSuccess: async response => {
      if (isIos) {
        SavePassword.promptDialog({
          username: form.getValues('email'),
          password: form.getValues('password')
        })
        .then(() => {})
        .catch((err) => console.error('promptDialog failure', err));
      }
      
      if (!response.two_factor) {
        if (isNative) {
          const token = extractToken((response as LoginResponse).bootstrapData);
          if (token) await saveToken(token);
        }

        handleSuccess(response);
      }
    },
    onError: r => onFormQueryError(r, form),
  });
}

export function useHandleLoginSuccess() {
  const navigate = useNavigate();
  const {getRedirectUri} = useAuth();
  
  return useCallback(
    async (response: LoginResponse) => {
      appPushNotificationManager.syncToken();
      setBootstrapData(response.bootstrapData);
      navigate(getRedirectUri(), {replace: true});
    },
    [navigate, getRedirectUri],
  );
}

// function login(payload: LoginPayload): Promise<Response> {
//   return apiClient.post('auth/login', payload).then(response => response.data);
// }

async function login(payload: LoginPayload): Promise<Response> {
  const body = {...payload};

  if (isNative) {
    body.token_name = getDeviceSync();
  }
  
  const res = await apiClient.post('auth/login', body);
  return res.data;
}
